import { useReviewAccess } from "@/services/reviews/useReviewAccess";
import useDashboardAccess from "@/hooks/useDashboardAccess";
import { Avatar, Collapse, Group, ScrollArea, Text, Title, UnstyledButton } from "@mantine/core";
import useGetMerchant from "@/pages/dashboard/profile/hooks/useGetMerchant";
import { useGetImage } from "@/utilities/helper/getImage";
import { IconCalendar, IconCash, IconChartPie, IconChevronDown, IconClipboardText, IconMoodSmile, IconReceipt, IconSettings, IconSpeakerphone, IconUsersGroup, IconWallet, IconWorld } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { MouseEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppointmentStatus } from "@/models/club";
import { useAuth } from "@/context/auth";
import { appointmentService } from "@/services/appointment";
import { useGetMerchantForMerchantUser } from "@/services/merchant/utilities";
import { isoDate } from "@/modules/mobileHome/format";
import { useRoleBadges } from "@/modules/mobileHome/useSectionBadges";
import useSubscription from "@/modules/subscription/useSubscription";
import classes from "./nav.module.css";
import { merchantSiteUrl } from "@/utilities/helper/merchantSite";

type Leaf = { link: string; label: string; badge?: "pendingReviews"; adminOnly?: boolean; adminOrRenter?: boolean; needsSubscription?: boolean };
type Entry = { key: string; label: string; icon: typeof IconCash; link?: string; children?: Leaf[] };

// Benchmarked on Fresha, Booksy and Mangomint: the two daily jobs sit on top as quick actions and the
// rest is grouped. The SMS balance lives in the header, so it has no menu item.
const entries: Entry[] = [
    { key: "dashboard", label: "داشبورد", icon: IconChartPie, link: "/dashboard" },
    {
        key: "sales", label: "فروش", icon: IconCash, children: [
            { link: "/dashboard/appointmentDeposit", label: "نوبت‌های آنلاین" },
            { link: "/dashboard/invoices", label: "فاکتورها" },
        ],
    },
    {
        key: "money", label: "مالی", icon: IconWallet, children: [
            { link: "/dashboard/finance", label: "امور مالی", adminOnly: true },
            { link: "/dashboard/padix-settlements", label: "تسویه‌های پدیکس", adminOrRenter: true },
            { link: "/dashboard/settlements/employees", label: "تسویه تیم", adminOnly: true },
        ],
    },
    {
        key: "customers", label: "مشتریان", icon: IconMoodSmile, children: [
            { link: "/dashboard/customers", label: "لیست مشتریان" },
            { link: "/dashboard/reviews", label: "نظرات مشتریان", badge: "pendingReviews" },
        ],
    },
    { key: "services", label: "خدمات", icon: IconClipboardText, link: "/dashboard/schedule/services" },
    { key: "marketing", label: "بازاریابی", icon: IconSpeakerphone, link: "/dashboard/marketing" },
    {
        key: "team", label: "تیم", icon: IconUsersGroup, children: [
            { link: "/dashboard/teams/teamMembers", label: "اعضای تیم" },
            { link: "/dashboard/teams/shiftsSchedule", label: "شیفت کاری" },
            { link: "/dashboard/barber-overview", label: "آمار تیم" },
        ],
    },
    {
        key: "settings", label: "تنظیمات", icon: IconSettings, children: [
            { link: "/dashboard/profile/edit", label: "اطلاعات مجموعه" },
            { link: "/dashboard/subscription", label: "اشتراک و پرداخت", adminOnly: true, needsSubscription: true },
        ],
    },
];

const isAt = (pathname: string, link: string) =>
    link === "/dashboard" ? pathname === link : pathname === link || pathname.startsWith(`${link}/`);
const fa = (n: number) => n.toLocaleString("fa-IR");

// Today's bookings that were not cancelled; a stylist counts only their own.
function useTodayCount(enabled: boolean, memberId: number | undefined, onlyMine: boolean) {
    const today = isoDate(new Date());
    return useQuery({
        queryKey: ["getDailyAppointment", today, today],
        queryFn: () => appointmentService.getDailyAppointment(today, today),
        enabled,
        staleTime: 60_000,
        select: (users) => users
            .filter((u) => !onlyMine || u.user?.id === memberId)
            .flatMap((u) => u.appointmentCalendar ?? [])
            .flatMap((day) => day.appointments ?? [])
            .filter((a) => a.status !== AppointmentStatus.Value2).length,
    }).data;
}

export default function Navbar() {
    const { isManager: isAdmin, canViewMenu } = useDashboardAccess();
    const { canRead: canReadReviews } = useReviewAccess();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const badges = useRoleBadges();
    const phone = useAuth().getUserByToken()?.phoneNumber ?? "";
    const self = useGetMerchantForMerchantUser(phone).data;
    const memberId = self?.id;
    const isRenter = Boolean((self as { isChairRenter?: boolean } | undefined)?.isChairRenter);
    const showCalendar = canViewMenu("/dashboard/calendar");
    const todayCount = useTodayCount(showCalendar, memberId, !isAdmin);

    const { data } = useGetMerchant();
    const { imageUrl } = useGetImage({ bucket: "merchant", image: data?.logo });

    const { available: hasSubscription } = useSubscription();
    const leafVisible = (leaf: Leaf) =>
        (leaf.adminOnly ? isAdmin : leaf.adminOrRenter ? isAdmin || isRenter : canViewMenu(leaf.link)) &&
        (!leaf.needsSubscription || hasSubscription) &&
        (leaf.link !== "/dashboard/reviews" || canReadReviews);
    const visible = entries
        .map((e) => (e.children ? { ...e, children: e.children.filter(leafVisible) } : e))
        .filter((e) => (e.children ? e.children.length > 0 : canViewMenu(e.link!)));

    const activeGroup = visible.find((e) => e.children?.some((c) => isAt(pathname, c.link)))?.key;
    const [open, setOpen] = useState<Record<string, boolean>>(() => (activeGroup ? { [activeGroup]: true } : {}));
    useEffect(() => {
        if (activeGroup) setOpen((o) => ({ ...o, [activeGroup]: true }));
    }, [activeGroup]);

    const go = (link: string) => (event: MouseEvent) => {
        event.preventDefault();
        navigate(link);
    };
    const badgeOf = (leaf: Leaf) => (leaf.badge ? badges[leaf.badge] ?? 0 : 0);

    return (
        <nav className={classes.navbar} style={{ background: "#232232" }}>
            <Group className={classes.merchantHeader} wrap="nowrap" gap="sm">
                <Avatar src={imageUrl || ""} style={{ flexShrink: 0 }}>
                    {data?.businessInfo?.title && data?.businessInfo?.title[0]}
                </Avatar>
                <Title c="white" order={3} size="sm" lineClamp={2} className={classes.merchantTitle} title={data?.businessInfo?.title ?? undefined}>
                    {data?.businessInfo?.title}
                </Title>
            </Group>
            <ScrollArea
                className={classes.navbarMain}
                scrollbars="y"
                scrollbarSize={6}
                type="hover"
                offsetScrollbars="y"
                overscrollBehavior="contain"
                classNames={{ scrollbar: classes.scrollbar, thumb: classes.scrollThumb }}
            >
                <div className={classes.menuContent}>
                    <div className={classes.quick}>
                        {canViewMenu("/dashboard/payments/add") && (
                            <a href="/dashboard/payments/add" className={classes.quickPrimary} data-active={isAt(pathname, "/dashboard/payments/add") || undefined} onClick={go("/dashboard/payments/add")}>
                                <IconReceipt size={22} stroke={1.6} />
                                <strong>ثبت فاکتور</strong>
                                <small>فروش جدید</small>
                            </a>
                        )}
                        {showCalendar && (
                            <a href="/dashboard/calendar" className={classes.quickSecondary} data-active={isAt(pathname, "/dashboard/calendar") || undefined} onClick={go("/dashboard/calendar")}>
                                <IconCalendar size={22} stroke={1.6} />
                                <strong>تقویم کاری</strong>
                                <small>{todayCount === undefined ? "نوبت‌های امروز" : todayCount ? `${fa(todayCount)} نوبت امروز` : "امروز نوبتی نیست"}</small>
                            </a>
                        )}
                    </div>

                    {visible.map((entry) => {
                        if (!entry.children) {
                            return (
                                <a key={entry.key} className={classes.link} data-active={isAt(pathname, entry.link!) || undefined} href={entry.link} onClick={go(entry.link!)}>
                                    <entry.icon className={classes.linkIcon} stroke={1.5} />
                                    <Text>{entry.label}</Text>
                                </a>
                            );
                        }
                        const isOpen = !!open[entry.key];
                        const total = entry.children.reduce((sum, c) => sum + badgeOf(c), 0);
                        return (
                            <div key={entry.key}>
                                <UnstyledButton
                                    className={classes.link}
                                    data-active={(!isOpen && activeGroup === entry.key) || undefined}
                                    onClick={() => setOpen((o) => ({ ...o, [entry.key]: !isOpen }))}
                                    aria-expanded={isOpen}
                                    aria-controls={`${entry.key}-submenu`}
                                    w="100%"
                                >
                                    <entry.icon className={classes.linkIcon} stroke={1.5} />
                                    <Text flex={1}>{entry.label}</Text>
                                    {total > 0 && !isOpen && <span className={classes.badge} aria-label={`${fa(total)} مورد در انتظار`}>{fa(total)}</span>}
                                    <IconChevronDown size={18} style={{ transform: isOpen ? "rotate(180deg)" : undefined, transition: "transform 150ms ease" }} />
                                </UnstyledButton>
                                <Collapse in={isOpen} id={`${entry.key}-submenu`}>
                                    {entry.children.map((child) => {
                                        const count = badgeOf(child);
                                        return (
                                            <a key={child.link} className={`${classes.link} ${classes.subLink}`} data-active={isAt(pathname, child.link) || undefined} href={child.link} onClick={go(child.link)}>
                                                <Text size="sm" flex={1}>{child.label}</Text>
                                                {count > 0 && <span className={classes.badge} aria-label={`${fa(count)} نظر در انتظار تأیید`}>{fa(count)}</span>}
                                            </a>
                                        );
                                    })}
                                </Collapse>
                            </div>
                        );
                    })}
                </div>
            </ScrollArea>
            {isAdmin && <div className={classes.footer}>
                <a href={merchantSiteUrl(data?.businessInfo?.subDomain)} target="_blank" className={classes.link}>
                    <Group gap="0" px="md">
                        <IconWorld className={classes.linkIcon} stroke={1} />
                        <Text>مشاهده وبسایت</Text>
                    </Group>
                </a>
            </div>}
        </nav>
    );
}
